import type { ContenuVitrine } from '@/features/marketing/i18n/contenu';

/**
 * **Le contenu français** — celui qui existait, rassemblé (#2374).
 *
 * Rien n'y est inventé : chaque module cité correspond à un écran qui existe, chaque geste mobile
 * décrit à un écran réel. Pas de logo client non consenti, pas de témoignage, pas de chiffre de
 * croissance.
 */
export const CONTENU_FR: ContenuVitrine = {
  trialLabel: 'Demander une démo',
  trialLabelCourt: 'Démo',
  navLabels: {
    '/': 'Accueil',
    '/fonctionnalites': 'Fonctionnalités',
    '/avantages': 'Avantages',
    '/applications-mobiles': 'Applications mobiles',
    '/espace-web': 'Espace web',
    '/tarifs': 'Tarifs',
    '/le-metier': 'Le métier',
    '/modules': 'Modules',
  },
  hero: {
    badge: 'L’ERP des sociétés de portage salarial',
    titreDebut: 'Tout le portage,',
    titreAccent: 'du contrat à la facture',
    accroche:
      'Suivora tient le métier de bout en bout — dossiers des portés, missions, comptes-rendus d’activité, notes de frais, validation RH et facturation. Avec deux applications mobiles : une pour vos consultants, une pour vos gestionnaires.',
    mentionSansEngagement:
      'Sans engagement — une démonstration commentée, du contrat à la facture.',
    preuves: [
      'Hébergement en France',
      'Cloisonné par organisation',
      'Deux applications iOS & Android',
    ],
  },
  metier: {
    eyebrow: 'Le métier, pas un outil de plus',
    title: 'Une société de portage, ce n’est pas un tableur de jours',
    lead: 'C’est un dossier salarié à tenir, une activité à faire valider, une facture à émettre et une rémunération à verser — chaque mois, pour chaque porté.',
    points: [
      {
        title: 'Le porté, de son contrat à sa sortie',
        text: 'Régime CDI en portage ou association, pièces d’identité, coordonnées bancaires, avenants : le dossier se constitue par un parcours guidé et se signe électroniquement, sans échange de PDF par mail.',
      },
      {
        title: 'Le mois déclaré, puis validé',
        text: 'Compte-rendu d’activité, absences, notes de frais et titres-restaurant arrivent au même endroit. Vos RH disposent d’un poste de validation dédié, pas d’une boîte de réception.',
      },
      {
        title: 'La facture client et la rémunération',
        text: 'La facturation se déclenche depuis la mission et l’activité validée. Les distributions de dividendes des associés suivent le même circuit, avec la même trace.',
      },
    ],
  },
  modules: {
    eyebrow: 'Couverture fonctionnelle',
    title: 'Un ERP complet, pas un module qu’il faudra compléter',
    lead: 'Tout ce qui suit est dans le produit aujourd’hui, dans la même base et sous les mêmes droits.',
    domaines: [
      {
        title: 'Consultants & contrats',
        text: 'Le dossier du porté, complet et à jour.',
        items: [
          'Annuaire et fiches consultants',
          'Onboarding guidé',
          'Régimes CDI portage et association',
          'Documents et signature électronique',
          'Absences et congés',
        ],
      },
      {
        title: 'Activité & justificatifs',
        text: 'Ce que le porté déclare, ce que les RH valident.',
        items: [
          'Comptes-rendus d’activité mensuels',
          'Notes de frais et barème kilométrique',
          'Titres-restaurant',
          'Poste de validation RH',
        ],
      },
      {
        title: 'Commerce & missions',
        text: 'D’où vient l’affaire, et qui elle rémunère.',
        items: [
          'Clients et signataires',
          'Missions et affectations',
          'Apporteurs d’affaires et commissions',
          'Cooptation',
        ],
      },
      {
        title: 'Finance & pilotage',
        text: 'Ce qui sort en facture, ce qui remonte en chiffre.',
        items: [
          'Facturation par mission',
          'Dividendes des associés',
          'Rapports et exports',
          'Tableaux de bord et statistiques',
        ],
      },
    ],
    extras: [
      'Rôles et permissions',
      'Journal d’audit',
      'Imports en masse',
      'Annonces internes',
      'Base de connaissance',
      'Support',
      'Intégrations',
      'Demandes de suppression',
    ],
  },
  apercu: {
    eyebrow: 'Aller plus loin',
    title: 'Trois pages pour le détail',
    lead: 'Les applications mobiles, l’espace web et les tarifs — l’essentiel ici, le détail sur leur page.',
    teasers: [
      {
        eyebrow: 'Applications mobiles',
        title: 'Vos consultants, sans passer par un ordinateur',
        text: 'CRA, notes de frais et documents depuis iOS et Android — et les validations pour ceux qui les attendent.',
      },
      {
        eyebrow: 'Espace web',
        title: 'Deux espaces, un seul produit',
        text: 'Le consultant et l’administration du cabinet : chacun ne voit que le sien, sur les mêmes données.',
      },
      {
        eyebrow: 'Tarifs',
        title: 'Vous payez au porté facturable',
        text: 'La grille en vigueur, telle qu’elle est facturée — dégressive à mesure que vous grandissez.',
      },
    ],
  },
  clients: {
    eyebrow: 'Ils nous font confiance',
    title: 'Des cabinets qui pilotent leur activité avec Suivora',
  },
  /*
    ⚠️ **Des propositions, pas des témoignages** (#2523) : chaque texte se fait relire et corriger par
    la personne qui le portera, et ne paraît qu'une fois son accord reporté dans `temoignages.ts`.
  */
  temoignages: {
    eyebrow: 'Ils en parlent',
    title: 'Ce que Suivora a changé dans leur quotidien',
    lead: 'Une fonctionnalité, une personne qui s’en sert, et ce qui a changé pour elle.',
    pause: 'Mettre en pause',
    reprendre: 'Reprendre le défilement',
    choisir: 'Choisir un témoignage',
    items: [
      {
        fonctionnalite: 'Dépôt sur le portail du client',
        fonction: 'Assistante de direction',
        citation:
          'Certains de nos clients ne veulent ni facture ni CRA par e-mail : il faut les déposer sur leur propre portail. Ces consignes vivaient dans nos têtes, et un oubli se découvrait à la relance. Aujourd’hui, le portail et les références à rappeler sont enregistrés sur le client ou la mission, la facture m’attend dans « À déposer », et je note la date du dépôt. Quand je relance, le client lit quand elle a été déposée.',
      },
      {
        fonctionnalite: 'Bons de commande suivis',
        fonction: 'Consultant porté',
        citation:
          'Je ne savais jamais où j’en étais de mon bon de commande. Maintenant je le vois pendant que je remplis mon CRA : ce que le client a commandé, ce que j’ai déjà déclaré, ce qu’il reste. Quand la fin approche, j’en parle au client avant la facture, pas après.',
      },
      {
        fonctionnalite: 'Communiqués aux consultants',
        fonction: 'Assistante RH',
        citation:
          'Nos informations aux consultants partaient par e-mail et se perdaient dans les boîtes. Avec les communiqués, elles arrivent dans leur espace, et je vois qui les a lues. Je reviens vers ceux qui ne l’ont pas fait, au lieu de tout renvoyer à tout le monde.',
      },
      {
        fonctionnalite: 'Signature électronique intégrée',
        fonction: 'Associé',
        citation:
          'Faire signer un document aux associés, c’était un envoi par personne et un tableau pour savoir qui avait signé. Aujourd’hui je dépose le PDF, je choisis les signataires et je suis le tout au même endroit — sans abonnement à un outil de signature en plus.',
      },
      {
        fonctionnalite: 'Du CRA validé à la facture',
        fonction: 'Responsable facturation',
        citation:
          'Un CRA validé devient un brouillon de facture : les jours viennent du CRA, le tarif de la mission. Je relis et j’émets, je ne recopie plus rien — et je ne découvre plus d’écart devant le client.',
      },
    ],
  },
  ora: {
    eyebrow: 'Assistant intégré',
    title: 'Ora répond aux questions que personne n’a le temps de reposer',
    lead: 'Chaque cabinet répond dix fois par mois aux mêmes questions : quand suis-je remboursé, pourquoi ce CRA a été refusé, qui valide avant qui. Ora y répond depuis la documentation du produit, dans les deux espaces.',
    nom: 'Ora',
    nature: 'Assistant IA',
    points: [
      {
        title: 'Elle cite ce qu’elle a lu',
        text: 'Chaque réponse indique les sections de documentation sur lesquelles elle s’appuie. On peut vérifier — c’est ce qui sépare un assistant d’un oracle.',
      },
      {
        title: 'Elle n’invente pas',
        text: '« Ce n’est pas dans la documentation » est une réponse qu’elle donne, et c’est la bonne. Une règle de paie plausible mais fausse ne se remarque pas : elle s’affiche, on la croit, on agit dessus.',
      },
      {
        title: 'Pour vos portés comme pour vos gestionnaires',
        text: 'Deux écrans, un même socle : le consultant demande comment déclarer ses frais, le gestionnaire comment se passe l’entrée d’un consultant.',
      },
    ],
    limite:
      'Ora répond sur le fonctionnement du produit, pas sur un dossier : elle ne lit ni CRA, ni montant, ni solde de congés, et le dit plutôt que de le deviner. Les questions sont traitées dans l’Union européenne.',
  },
  confiance: {
    eyebrow: 'Confiance',
    title: 'Les questions qu’on vous posera avant de signer',
    lead: 'Vous manipulez des contrats de travail, des bulletins et des coordonnées bancaires. Voici ce que le produit garantit, mécanisme par mécanisme.',
    points: [
      {
        title: 'Cloisonnement par organisation',
        text: 'Chaque organisation est servie sur son propre sous-domaine, avec ses données, ses comptes et ses droits — rien n’est partagé entre deux entités.',
      },
      {
        title: 'Rôles et permissions explicites',
        text: 'Un accès est un droit accordé, pas un statut hérité. Les rôles se composent module par module.',
      },
      {
        title: 'Journal d’audit',
        text: 'Les actions sensibles laissent une trace horodatée, consultable, qui survit à la personne qui les a faites.',
      },
      {
        title: 'Vos obligations RGPD',
        text: 'Les demandes de suppression de compte sont instruites dans l’outil, et l’hébergement des données est en France.',
      },
    ],
  },
  tarifs: {
    titre: {
      eyebrow: 'Tarifs',
      title: 'Vous payez au nombre de consultants portés',
      lead: 'Un modèle qui suit votre activité plutôt que votre nombre de licences — et qui coûte moins cher par porté à mesure que vous grandissez.',
    },
    points: [
      {
        title: 'Un seul compteur : les portés que vous avez facturés',
        text: 'Un consultant compte s’il a au moins une facture émise sur les six derniers mois. Ni par module, ni par utilisateur du back-office, ni par document signé.',
      },
      {
        title: 'Dégressif par palier',
        text: 'Le tarif du palier atteint s’applique à tout le volume. Plus vous portez de consultants, moins chacun vous coûte.',
      },
      {
        title: 'Tout est compris',
        text: 'Chaque module, les deux espaces et les applications mobiles. Pas d’option facturée à part, pas d’engagement de durée.',
      },
    ],
    grilleEnVigueur: 'Grille en vigueur',
    grilleLead: 'Les paliers tels qu’ils sont facturés — lus chez notre facturier, pas recopiés.',
    palier: 'Palier',
    tarif: 'Tarif',
    parPorteEtParMois: 'par mois et par porté',
    auForfaitParMois: 'au forfait, par mois',
    etUnForfaitDe: 'et un forfait de',
    exemple: 'Un exemple',
  },
  avantages: {
    titre: {
      eyebrow: 'Avantages',
      title: 'Ce que Suivora fait autrement',
      lead: 'Huit partis pris, et ce qu’ils changent une fois le produit en service.',
    },
    chiffres: {
      eyebrow: 'En chiffres',
      title: 'Ce qui est dans le produit aujourd’hui',
      lead: 'Des nombres vérifiables, pas des arrondis : ils décrivent ce qui est livré.',
    },
    differences: [
      {
        eyebrow: 'Sur le terrain',
        title: 'Un CRA se valide depuis le téléphone',
        problem:
          'Valider demande souvent un poste de travail, un export tableur et une relance par courriel — et la validation attend le retour au bureau.',
        answer:
          'Le validateur reçoit une notification et répond en deux gestes. Le client extérieur, lui, valide par un lien, sans compte à créer.',
      },
      {
        eyebrow: 'Droits',
        title: 'Quatre-vingt-seize droits, pas trois profils',
        problem:
          'Un « profil RH » qui ouvre tout ce qu’une RH pourrait faire donne aussi ce qu’elle ne doit pas voir : les salaires, les dividendes, la politique de sécurité.',
        answer:
          'Chaque écran a sa clé, et les réglages se découpent onglet par onglet. Un rôle qui règle les délais de paiement n’ouvre pas la politique de mot de passe. Vous nommez vos rôles, et l’éditeur montre ce que chaque case accorde réellement.',
      },
      {
        eyebrow: 'Chiffres',
        title: 'Un chiffre absent n’est jamais affiché comme zéro',
        problem:
          'Un tableau de bord qui remplace une donnée manquante par zéro affiche « 0 € facturé » sur un cabinet qui facture — et personne ne s’en aperçoit, parce qu’un zéro ne se remarque pas.',
        answer:
          'Ce que le serveur ne sert pas ne s’affiche pas. Un total partiel dit qu’il est partiel, et les écrans vérifient leurs propres nombres — un effectif se recoupe avec ses entrées et ses sorties.',
      },
      {
        eyebrow: 'Outils',
        title: 'Vos outils restent les vôtres',
        problem:
          'Un ERP qui impose sa paie, sa facturation et sa signature oblige à changer quatre outils pour en adopter un.',
        answer:
          'Chaque famille se branche séparément — paie, facturation, signature, banque. Ce que le connecteur retenu sait faire est annoncé, et ce qu’il ne sait pas faire aussi.',
      },
      {
        eyebrow: 'Grands comptes',
        title: 'Le client qui veut son portail l’a',
        problem:
          'Les grands comptes n’acceptent plus la facture par e-mail : elle se dépose sur leur espace fournisseur. L’outil l’envoie quand même, le dépôt se fait à côté, et plus personne ne sait s’il a eu lieu — jusqu’à l’impayé.',
        answer:
          'Le portail se règle sur le client, ou pour une seule mission. Rien ne part par e-mail : la facture et le CRA attendent « à déposer », l’accès sous la main et chiffré. Le dépôt se consigne, et la relance écrit « déposée sur votre portail le … ».',
      },
      {
        eyebrow: 'Migration',
        title: 'On reprend l’existant, pas seulement les noms',
        problem:
          'Une reprise limitée aux fiches oblige à ressaisir des années de CRA, de frais et de factures — ou à garder l’ancien outil ouvert pour l’historique.',
        answer:
          'Sept imports enchaînés couvrent les clients, les consultants, les missions, les contacts, les CRA, les notes de frais et les factures. Chacun se joue à blanc d’abord, et dit ce qu’il ignorera.',
      },
      {
        eyebrow: 'Marque',
        title: 'Vos consultants voient votre nom',
        problem:
          'Un outil qui affiche sa propre marque au porté rappelle chaque mois que le cabinet loue un logiciel.',
        answer:
          'Sous-domaine, logo, couleur, mentions légales et émetteur des documents sont ceux du cabinet — dans l’application comme sur les courriers et les factures.',
      },
      {
        eyebrow: 'Régimes',
        title: 'Portage salarial et association, dans le même produit',
        problem:
          'Un cabinet qui pratique les deux régimes tient souvent deux outils, ou un outil et un tableur.',
        answer:
          'Comptes d’activité d’un côté, quote-parts de l’autre, avec leurs écrans et leurs droits. Un cabinet ne voit que le régime qu’il pratique.',
      },
      {
        eyebrow: 'Traçabilité',
        title: 'Ce qui touche à l’argent ou aux droits est consigné',
        problem:
          'Sans journal, une question simple — qui a changé ce taux, quand — n’a pas de réponse, et se règle de mémoire.',
        answer:
          'Journal d’audit consultable et exportable, alimenté par les écritures elles-mêmes plutôt que par une intention de journaliser.',
      },
    ],
    figures: [
      { label: 'applications mobiles', detail: 'consultant et gestionnaire, natives' },
      { label: 'droits dans votre éditeur', detail: 'répartis en 17 domaines' },
      { label: 'imports de reprise', detail: 'avec essai à blanc' },
      { label: 'familles de connecteurs', detail: 'paie, facturation, signature, banque' },
    ],
  },
  espaceWeb: {
    titre: {
      eyebrow: 'Espace web',
      title: 'Et sur grand écran, toute la surface',
      lead: 'Le mobile porte le geste quotidien ; le web porte ce qui demande de la place — le mois entier, les listes, les totaux, les exports et les décisions qui engagent. Les deux espaces y ont le leur.',
    },
    moments: [
      {
        alt: 'La feuille de temps d’un consultant sur navigateur',
        title: 'Le mois entier sous les yeux',
        text: 'Le calendrier complet, les jours travaillés, les absences et les astreintes au même endroit — et « Remplir le mois » pour les cas où rien ne change. Le téléphone fait la retouche quotidienne ; l’ordinateur fait le mois.',
      },
      {
        alt: 'Le suivi financier d’un consultant sur navigateur',
        title: 'Ce qu’il facture, ce qu’il touche',
        text: 'Chiffre d’affaires, salaire net, dividendes et notes de frais sur la même échelle, mois après mois, avec le taux de conversion. C’est la lecture qu’un écran de téléphone ne peut que résumer.',
      },
    ],
    points: [
      'Les listes et les tableaux, paginés et filtrables',
      'La facturation et les exports comptables',
      'Les statistiques et les rapports',
      'Les rôles, les permissions et le journal d’audit',
    ],
    espaces: [
      {
        tag: 'Consultant',
        title: 'Il déclare, il justifie, il signe',
        text: 'Sur mobile au quotidien, sur le web quand il faut un grand écran. Le même compte, les mêmes données.',
        items: [
          'CRA, absences et notes de frais',
          'Documents et signature électronique',
          'Chiffre d’affaires et missions',
          'Cooptation et annonces internes',
        ],
      },
      {
        tag: 'Administration & RH',
        title: 'Elles pilotent, valident, facturent',
        text: 'Le back-office complet de la société de portage, avec des droits accordés module par module.',
        items: [
          'Consultants, clients et missions',
          'Validation des CRA et des frais',
          'Facturation et dividendes',
          'Statistiques, rapports et audit',
        ],
      },
    ],
  },
  espaces: {
    titre: {
      eyebrow: 'Deux espaces, une seule vérité',
      title: 'Chacun son interface, les mêmes données',
      lead: 'Ce que le consultant déclare le 30 est ce que les RH valident le 2, et ce que la facture reprend le 5. Sans export, sans ressaisie, sans version parallèle.',
    },
    cycle: [
      {
        title: 'Le porté déclare',
        text: 'CRA du mois, absences et notes de frais, saisis depuis l’application mobile au fil des jours.',
      },
      {
        title: 'Les RH valident',
        text: 'Un poste de validation dédié reprend les écarts de jours et les justificatifs manquants, sans ressaisie.',
      },
      {
        title: 'La facture part',
        text: 'La facturation s’appuie sur la mission et l’activité validée. Commissions d’apporteurs et dividendes suivent.',
      },
    ],
  },
  mobile: {
    titre: {
      eyebrow: 'Applications mobiles',
      title: 'Deux applications natives, pas un site replié sur un écran',
      lead: 'Une pour vos consultants, une pour vos gestionnaires. C’est là que se joue l’exploitation réelle : un CRA qu’on remplit dans le métro rentre à l’heure, et une validation qu’on peut faire en déplacement ne bloque pas la facturation.',
    },
    consultantMoments: [
      {
        alt: 'Le compte-rendu d’activité et les notes de frais dans l’application consultant',
        title: 'Son mois se déclare au doigt',
        text: 'Un tap pose une demi-journée, deux une journée pleine, un appui long bascule en télétravail — l’application le rappelle en tête de calendrier. Les frais suivent, justificatif photographié à la volée, et ce qui manque est signalé avant l’envoi.',
      },
      {
        alt: 'L’accueil du consultant et le détail de ses finances mois par mois',
        title: 'Ses chiffres, sans avoir à les demander',
        text: 'Chiffre d’affaires du mois et de la mission, avancement du compte-rendu, frais engagés : tout est en page d’accueil. Le détail va jusqu’au mois — CA facturé, salaire, notes de frais — et le relevé se télécharge.',
      },
    ],
    consultantFeatures: [
      {
        title: 'Le CRA se remplit au doigt',
        text: 'Un tap pose une demi-journée, deux une journée pleine, un appui long bascule en télétravail. Un mois se déclare en moins d’une minute, sans clavier.',
      },
      {
        title: 'Le ticket se lit tout seul',
        text: 'Photographiez le justificatif : le montant, la date et l’enseigne en sont repris, et vous n’avez plus qu’à vérifier. Ce que vous avez déjà saisi n’est jamais écrasé.',
      },
      {
        title: 'Le justificatif ne quitte pas nos serveurs',
        text: 'La lecture se fait là où l’image est déposée : elle n’est ni téléchargée, ni copiée, ni envoyée ailleurs. Elle ne sort ni de nos serveurs, ni de l’Europe.',
      },
      {
        title: 'Une note de frais, une photo',
        text: 'Le justificatif est capturé à la volée. Les frais kilométriques appliquent le barème en vigueur, et l’app signale le justificatif manquant avant l’envoi.',
      },
      {
        title: 'Les documents se signent sur le téléphone',
        text: 'Contrats, avenants et attestations partent en signature électronique et se signent depuis l’application, sans imprimante ni scanner.',
      },
      {
        title: 'Sa situation, sans avoir à la demander',
        text: 'Chiffre d’affaires du mois, CA cumulé sur la mission, ancienneté, frais remboursés : le porté a ses chiffres en page d’accueil.',
      },
    ],
    pushExamples: [
      'CRA à envoyer',
      'Justificatif manquant',
      'Document à signer',
      'Frais remboursés',
    ],
    adminMoments: [
      {
        alt: 'L’accueil de l’application d’administration et la validation d’un compte-rendu d’activité',
        title: 'Voir ce qui attend, et le traiter',
        text: 'L’accueil dit combien de dossiers réclament une décision, et par lequel commencer. Un compte-rendu rentré la veille de la facturation se valide dans la foulée, sans rouvrir le back-office.',
      },
      {
        alt: 'La liste des missions et la création d’une mission en trois étapes',
        title: 'Monter une mission entre deux rendez-vous',
        text: 'Client, période, affectation : l’essentiel tient en une étape, les deux suivantes affinent le contrat et peuvent attendre. La mission existe avant le retour au bureau.',
      },
      {
        alt: 'La création d’un consultant et l’annuaire des consultants',
        title: 'Faire entrer un consultant',
        text: 'On ne saisit que l’essentiel : le porté complète lui-même son profil par le lien d’onboarding. L’annuaire montre ensuite qui est en mission, qui reste à valider, et ce qui manque encore.',
      },
    ],
    adminFeatures: [
      {
        title: 'Valider un CRA rentré en retard',
        text: 'Un consultant envoie son compte-rendu le 3 au soir, la veille de la facturation. La validation se fait dans l’application, sans rouvrir le back-office.',
      },
      {
        title: 'Onboarder un consultant',
        text: 'Créer le compte, lancer le parcours d’arrivée et suivre les pièces qui manquent encore — un nouveau porté n’attend pas votre retour de déplacement.',
      },
      {
        title: 'Créer une mission',
        text: 'Client, période, affectation du consultant : une mission se monte depuis le téléphone, entre deux rendez-vous, et non le soir venu.',
      },
      {
        title: 'Ne rien laisser passer',
        text: 'Tout ce qui attend une décision remonte en notification : CRA à valider, note de frais, document à signer, demande d’absence.',
      },
    ],
  },
  fonctionnalites: {
    titre: {
      eyebrow: 'Fonctionnalités',
      title: 'Ce que vous ouvrez le lundi matin',
      lead: 'Le détail de chaque domaine, tel qu’il existe aujourd’hui dans le produit. Rien au futur.',
    },
    transversal: {
      eyebrow: 'Sans supplément',
      title: 'Ce qui traverse tous les écrans',
      lead: 'Ces sept-là n’appartiennent à aucun module : ils valent partout, pour tout le monde.',
      points: [
        {
          title: 'Deux applications mobiles',
          text: 'Une pour le consultant — CRA, frais, absences, bulletins. Une pour le gestionnaire — valider, assigner, suivre. Ce ne sont pas des sites au format téléphone.',
        },
        {
          title: 'Connecteurs au choix',
          text: 'Paie, facturation, signature électronique, banque : chaque famille se choisit indépendamment, et ce que le connecteur retenu sait faire est annoncé à l’écran.',
        },
        {
          title: 'Reprise de l’existant',
          text: 'Sept imports enchaînés — clients, consultants, missions, contacts, CRA, notes de frais, factures — avec un essai à blanc avant d’écrire quoi que ce soit.',
        },
        {
          title: 'Aux couleurs du cabinet',
          text: 'Sous-domaine, logo, couleur d’accent, mentions légales et émetteur des documents : le consultant voit la marque de son employeur, pas la nôtre.',
        },
        {
          title: 'Signature électronique incluse',
          text: 'Contrats et avenants se signent dans le produit, avec horodatage et piste d’audit. Un prestataire externe peut prendre le relais.',
        },
        {
          title: 'Communication interne',
          text: 'Communiqués, blog, publications sociales et notifications : ce que le cabinet a à dire à ses portés part d’ici.',
        },
        {
          title: 'Ora, l’assistant intégré',
          text: 'Un assistant IA qui répond sur le fonctionnement du produit, en citant les sections de documentation qu’il a lues. Dans les deux espaces, sans supplément.',
        },
      ],
    },
    galerie: {
      eyebrow: 'En images',
      title: 'Quatre écrans de plus',
      lead: 'Ceux qu’aucun domaine ne porte à lui seul, et qui disent le mieux comment le produit travaille.',
      images: [
        {
          alt: 'Écran de facturation : ses compteurs, dont les avoirs comptés à part, et la liste des factures',
          caption:
            'La facturation en un écran : ce qui est facturé, ce qui reste à émettre, ce qui attend un règlement — et un avoir compté à part, jamais fondu dans le total.',
        },
        {
          alt: 'Liste des rôles de l’organisation, chaque carte disant son nom, sa description et son nombre de permissions',
          caption:
            'Les rôles sont les vôtres : vous les nommez, vous choisissez ce que chacun ouvre, et le nombre de permissions accordées se lit sur la carte.',
        },
        {
          alt: 'Séquence de reprise : les sept imports dans l’ordre, chacun disant de quoi il dépend',
          caption:
            'La reprise, dans l’ordre où elle se joue. Chaque étape dit de quoi elle dépend, et rien n’est écrit tant que vous n’avez pas simulé.',
        },
        {
          alt: 'Écran des intégrations : la chaîne de traitement et les quatre cartes de connecteurs',
          caption:
            'Paie, facturation, signature, banque : quatre maillons, choisis séparément. Chaque connecteur annonce ce qu’il sait faire — et ce qu’il ne sait pas faire.',
        },
      ],
    },
    domaines: [
      {
        title: 'Consultants et contrats',
        lead: 'Le dossier du porté, de son arrivée à sa sortie.',
        items: [
          {
            label: 'Fiche consultant complète',
            detail:
              'État civil, coordonnées, contact d’urgence, visite médicale, titre de séjour, coordonnées bancaires — chaque champ se corrige depuis l’écran, sans passer par un import.',
          },
          {
            label: 'Onboarding guidé',
            detail:
              'Le porté remplit son dossier depuis son espace ; la RH contrôle puis active le compte. Les dossiers en attente se voient dès le tableau de bord.',
          },
          {
            label: 'Entrées et sorties datées',
            detail:
              'Sortie prévue et sortie effective sont deux dates distinctes : un préavis en cours n’est pas un départ. Le statut d’emploi suit la sortie effective, jamais l’inverse.',
          },
          {
            label: 'Deux régimes de rémunération',
            detail:
              'Portage salarial classique et régime associé en dividendes coexistent, avec leurs écrans propres — un cabinet ne voit que ce qu’il pratique.',
          },
          {
            label: 'Absences et congés',
            detail:
              'Solde, calendrier, validation. Un gestionnaire peut déclarer à la place de quelqu’un, et c’est un droit distinct de celui de valider.',
          },
        ],
      },
      {
        title: 'Activité et justificatifs',
        lead: 'Ce que le porté déclare, ce que la RH valide.',
        items: [
          {
            label: 'Comptes rendus d’activité',
            detail:
              'Un CRA par mission et par mois, avec activités, astreintes et télétravail. La grille de saisie tient dans un écran de téléphone.',
          },
          {
            label: 'Validation externe',
            detail:
              'Le client valide le CRA par un lien, sans compte à créer. Sa réponse revient dans le dossier et part à la comptabilité.',
          },
          {
            label: 'Notes de frais',
            detail:
              'Justificatif photographié, catégorie, TVA, refacturation au client. Le barème kilométrique et le grand déplacement sont calculés, pas saisis.',
          },
          {
            label: 'Titres-restaurant',
            detail:
              'Commande mensuelle déduite des jours travaillés et des absences, transmise au prestataire.',
          },
          {
            label: 'Poste de validation',
            detail:
              'Une file unique pour les CRA et les notes de frais, avec le motif de refus écrit une fois et lu par la personne concernée.',
          },
        ],
      },
      {
        title: 'Commerce et missions',
        lead: 'D’où vient l’affaire, et qui elle engage.',
        items: [
          {
            label: 'Clients, contacts et signataires',
            detail:
              'Trois rôles distincts sur une même société : qui signe, qui valide les CRA, qui reçoit les factures. Les confondre coûte une facture perdue.',
          },
          {
            label: 'Facturation par intermédiaire',
            detail:
              'Le client final et la société facturée peuvent différer, et la chaîne se lit sur la fiche des deux.',
          },
          {
            label: 'Missions, bons de commande et renouvellements',
            detail:
              'Jours vendus, jours offerts, période et référence du bon. Un renouvellement s’enchaîne sans ressaisir la mission.',
          },
          {
            label: 'Apport d’affaires et cooptation',
            detail:
              'Un apporteur suit ses dossiers depuis son propre espace, et ses commissions se calculent sur le barème du cabinet.',
          },
        ],
      },
      {
        title: 'Facturation et finance',
        lead: 'Ce qui part en facture, ce qui rentre en banque.',
        items: [
          {
            label: 'Factures et avoirs',
            detail:
              'Émission depuis la mission et le CRA validé, avec les mentions de TVA que le régime impose — franchise, autoliquidation intracommunautaire, hors champ.',
          },
          {
            label: 'Relance des impayés',
            detail:
              'Par client ou en une passe, avec l’historique de ce qui est déjà parti. Relancer est un droit distinct d’émettre.',
          },
          {
            label: 'Dépôt sur le portail du client',
            detail:
              'Pour le client qui refuse l’e-mail — Coupa, Ariba, Chorus Pro : rien ne part, la facture et le CRA attendent « à déposer », l’accès au portail est gardé chiffré, et le dépôt se consigne avec sa date. La relance la cite.',
          },
          {
            label: 'Rapprochement bancaire',
            detail:
              'Le règlement est daté par la transaction rapprochée, pas par le jour où la synchronisation l’a vue. Une facture réglée sans transaction le dit plutôt que d’inventer une date.',
          },
          {
            label: 'Dividendes et comptes d’activité',
            detail:
              'Selon le régime : quote-part des associés, ou compte d’activité du porté avec sa réserve financière.',
          },
          {
            label: 'Escomptes et délais de paiement',
            detail:
              'Catalogues tenus par le cabinet, appliqués à la mission puis à la facture — jamais saisis deux fois.',
          },
        ],
      },
      {
        title: 'Pilotage',
        lead: 'Des chiffres qui se vérifient.',
        items: [
          {
            label: 'Tableau de bord',
            detail:
              'Les files qui attendent une action, et rien d’autre en tête d’écran : CRA, notes de frais, documents à signer, impayés.',
          },
          {
            label: 'Vues d’ensemble par domaine',
            detail:
              'Missions, Répertoire, Finance, Communication : chaque groupe a la sienne, avec les séries mensuelles sur trois ans.',
          },
          {
            label: 'Effectifs',
            detail:
              'Arrivées, sorties et effectif, séparés parce qu’un stock et un flux ne se lisent pas sur le même axe. La série annonce ce qu’elle ignore.',
          },
          {
            label: 'Bilan annuel',
            detail:
              'Facturé, encaissé, masse salariale, frais de gestion et délai moyen d’encaissement, par exercice.',
          },
          {
            label: 'Analytics et délivrabilité e-mail',
            detail:
              'Ce que les gens ouvrent, ce qui rebondit, et pourquoi. Un courrier qui n’arrive pas se voit avant que le client ne le signale.',
          },
        ],
      },
      {
        title: 'Administration',
        lead: 'Qui a le droit de quoi, et qui a fait quoi.',
        items: [
          {
            label: 'Rôles et permissions',
            detail:
              'Cent droits au catalogue, groupés par domaine. Les réglages se découpent onglet par onglet : régler un délai de paiement n’ouvre pas la politique de mot de passe.',
          },
          {
            label: 'Journal d’audit',
            detail:
              'Qui a modifié quoi, quand, et depuis où. Exportable, et les écritures sensibles y sont consignées sans exception.',
          },
          {
            label: 'Comptes portail',
            detail:
              'Invitation, réinitialisation, suppression de compte au sens RGPD, avec export des données de la personne.',
          },
          {
            label: 'Profils internes',
            detail:
              'Les collaborateurs du cabinet — salariés, alternants — avec leurs documents et leurs absences, distincts des portés.',
          },
        ],
      },
    ],
  },
  support: {
    badge: 'Support',
    titre: 'Une question, un incident : voici par où passer',
    accroche:
      'Toutes les demandes ne vont pas au même endroit. Celle qui touche à votre contrat ou à votre paie revient à la société qui vous porte ; celle qui touche au produit revient à son éditeur.',
    routesEyebrow: 'Trois chemins',
    routesTitre: 'À qui votre demande revient',
    routes: [
      {
        tag: 'Le plus direct',
        title: 'Depuis l’application',
        text: 'Les applications mobiles et l’espace web portent un écran « Nous contacter ». C’est le chemin le plus court : votre demande arrive déjà rattachée à votre compte et à votre société.',
      },
      {
        tag: 'Pour tout ce qui touche à votre contrat',
        title: 'Votre société de portage',
        text: 'Contrat, bulletin de paie, compte rendu d’activité, note de frais, accès à votre compte : c’est elle qui décide et qui répond. Suivora est l’outil qu’elle utilise, pas son employeur.',
      },
      {
        tag: 'Ce formulaire',
        title: 'L’éditeur du produit',
        text: 'Un dysfonctionnement de la plateforme, une question sur le produit, ou une demande relative à vos données que votre société ne peut pas traiter : le formulaire ci-contre arrive chez nous.',
      },
    ],
    formulaireEyebrow: 'Écrire à l’éditeur',
    formulaireTitre: 'Décrivez votre demande',
    formulaireTexte:
      'Nous vous répondons à l’adresse indiquée, et vous recevez une référence à citer si vous nous relancez.',
    sujets: ['Un dysfonctionnement', 'Une question sur le produit', 'Mes données personnelles'],
  },
  formulaire: {
    confirmation: 'Votre demande est arrivée',
    nom: 'Votre nom',
    nomPlaceholder: 'Prénom et nom',
    societe: 'Votre société',
    societePlaceholder: 'Raison sociale',
    email: 'Adresse électronique',
    emailPlaceholder: 'prenom.nom@societe.fr',
    telephone: 'Téléphone',
    facultatif: 'Facultatif',
    portes: 'Consultants portés',
    portesPlaceholder: 'Ex. 45',
    portesHint: 'Un nombre, même approximatif.',
    objet: 'Objet',
    objetPlaceholder: 'En une ligne',
    motifs: ['Découvrir le produit', 'Remplacer notre outil actuel', 'Chiffrage et tarifs'],
    autre: 'Autre',
    motifManquant: 'Choisissez un motif, ou « Autre ».',
    message: 'Votre message',
    messagePlaceholder:
      'Vos outils actuels, ce que vous cherchez à remplacer, vos contraintes — ce que vous voudriez voir pendant la démonstration.',
    envoyer: 'Envoyer ma demande',
    piege: 'Ne pas remplir',
    erreurIndisponible: 'Le formulaire n’est pas encore en service. Réessayez dans quelques jours.',
    erreurTropDeDemandes:
      'Trop de demandes envoyées depuis cette adresse. Réessayez dans quelques minutes.',
    erreurEnvoi: 'L’envoi a échoué. Votre message est toujours là — réessayez dans un instant.',
    emailRaison: 'C’est notre seul moyen de vous répondre.',
  },
  demo: {
    badge: 'Démonstration',
    titre: 'Voyez Suivora tourner, du contrat à la facture',
    accroche:
      'Nous vous montrons le produit en fonctionnement, sur un environnement de démonstration complet — et nous répondons à vos cas au fur et à mesure.',
    preuves: ['Démonstration commentée', 'Applications mobiles comprises'],
    etapesEyebrow: 'Après l’envoi',
    etapesTitre: 'Trois étapes, et rien à installer',
    etapes: [
      {
        title: 'Vous décrivez votre activité',
        text: 'Le nombre de consultants portés, vos régimes de contrat, vos outils actuels. Trois minutes suffisent.',
      },
      {
        title: 'Nous préparons le parcours',
        text: 'Nous choisissons ce que nous vous montrerons : les régimes de contrat et les modules qui correspondent à ce que vous faites.',
      },
      {
        title: 'Nous vous la présentons',
        text: 'Le produit en fonctionnement, écran par écran : un porté, une mission, un compte-rendu d’activité, des frais, jusqu’à la facture client. Vos questions au fur et à mesure.',
      },
    ],
    verrezTitre: 'Ce que vous verrez',
    verrez: [
      'Le parcours complet, du contrat à la facture client',
      'Les modules qui concernent votre activité',
      'Les deux applications mobiles, iOS et Android',
      'Le palier de la grille qui vous concerne, chiffré',
    ],
    formulaireEyebrow: 'Demande de démonstration',
    formulaireTitre: 'Parlez-nous de votre activité',
    formulaireTexte:
      'Nous vous répondons à l’adresse indiquée, avec le palier de la grille qui vous concerne.',
  },
  ctaFinal: {
    titre: 'Voir Suivora sur votre organisation',
    texte:
      'Une démonstration commentée, du contrat à la facture, sur un environnement préparé pour votre activité.',
    arguments: ['Démonstration commentée', 'Applications mobiles comprises'],
  },
  pied: {
    support: 'Support',
    mentionsLegales: 'Mentions légales',
    confidentialite: 'Confidentialité',
    conditions: 'Conditions générales',
  },
  autreLangue: 'English',
  autreLangueCourte: 'EN',
};
